import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BookOpen,
  FileText,
  FolderPlus,
  Info,
  Plus,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import { useCurrentUser } from '../hooks/useCurrentUser'
import type { Matter } from '../lib/types'

// ─── i18n labels ─────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '工作台',
    welcomePrefix: '欢迎回来, ',
    welcomeDesc: (days: number, analyses: number) =>
      `今天是您使用 AI 律师助手的第 ${days} 天，已处理 ${analyses} 次案件分析请求`,
    newCase: '新建案件',
    uploadKnowledge: '上传知识文件',
    activeCases: '进行中案件',
    kbCategories: '知识库分类',
    docDrafts: '文书草稿',
    aiAnalyses: '知识库文件',
    recentCases: '最近案件',
    viewAll: '查看全部',
    quickActions: '快捷操作',
    createCase: '创建新案件',
    createCaseDesc: '创建新案件并上传材料',
    uploadFile: '上传知识文件',
    uploadFileDesc: '将文件传送至知识库',
    queryLegal: '查询法律知识',
    queryLegalDesc: '检索法律知识库及案件资料',
    kbTitle: '知识库分类',
    addCategory: '添加新分类',
    files: '文件',
    statusPlanning: '规划中',
    statusActive: '进行中',
    statusClosed: '已完成',
    tip: '使用提示',
    tipText:
      'AI 律师助手仅提供参考分析与文书草稿功能，不能出具最终法律判断。所有生成内容需要经验律师确认后方可正式交付。系统基于社区法律基金会与您上传的案件材料进行综合分析。',
  },
  en: {
    pageTitle: 'Dashboard',
    welcomePrefix: 'Welcome back, ',
    welcomeDesc: (days: number, analyses: number) =>
      `Day ${days} using AI Lawyer Assistant — ${analyses} case analyses processed`,
    newCase: 'New Case',
    uploadKnowledge: 'Upload Knowledge',
    activeCases: 'Active Cases',
    kbCategories: 'KB Categories',
    docDrafts: 'Doc Drafts',
    aiAnalyses: 'KB Files',
    recentCases: 'Recent Cases',
    viewAll: 'View all',
    quickActions: 'Quick Actions',
    createCase: 'Create New Case',
    createCaseDesc: 'Create a case and upload materials',
    uploadFile: 'Upload Knowledge File',
    uploadFileDesc: 'Upload documents to knowledge base',
    queryLegal: 'Query Legal Knowledge',
    queryLegalDesc: 'Search legal knowledge and case data',
    kbTitle: 'Knowledge Categories',
    addCategory: 'Add new category',
    files: 'files',
    statusPlanning: 'Planning',
    statusActive: 'Active',
    statusClosed: 'Closed',
    tip: 'Usage Tip',
    tipText:
      'AI Lawyer Assistant provides reference analysis and draft generation only. All generated content must be reviewed by an experienced lawyer before formal delivery. The system analyses based on the Malaysian legal knowledge base and your uploaded case materials.',
  },
}

export function Dashboard() {
  const { lang } = useI18n()
  const { user } = useCurrentUser()
  const t = LABELS[lang] ?? LABELS.zh

  const [matters, setMatters] = useState<Matter[]>([])
  const [kbCategories, setKbCategories] = useState<{ key: string; name_zh: string; name_en: string; file_count: number }[]>([])
  const [stats, setStats] = useState({
    activeCases: 0,
    kbCategories: 0,
    docDrafts: 0,
    aiAnalyses: 0,
  })

  // 加载案件数据
  useEffect(() => {
    fetch('/matters?limit=50')
      .then((r) => r.json())
      .then((data: { items?: Matter[]; total?: number }) => {
        if (data.items && Array.isArray(data.items)) {
          setMatters(data.items)
          const active = data.items.filter((m) => m.status === 'active').length
          setStats((s) => ({ ...s, activeCases: active }))
        }
      })
      .catch(() => {})
  }, [])

  // 加载知识库分类（用户个人知识库，不是公共法律底座）
  useEffect(() => {
    fetch('/knowledge/categories?shared=false')
      .then((r) => r.json())
      .then((data: { categories?: { key: string; name_zh: string; name_en: string; file_count: number }[] }) => {
        if (data.categories) {
          setKbCategories(data.categories)
          const totalFiles = data.categories.reduce((sum, c) => sum + (c.file_count ?? 0), 0)
          setStats((s) => ({
            ...s,
            kbCategories: data.categories!.length,
            aiAnalyses: totalFiles,
          }))
        }
      })
      .catch(() => {})
  }, [])

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Welcome Banner */}
        <div className="rounded-2xl bg-gradient-to-r from-navy-950 to-blue-700 p-6 text-white">
          <h2 className="text-xl font-bold mb-1">
            {t.welcomePrefix}
            {user?.display_name ?? '---'}
          </h2>
          <p className="text-sm text-white/80 mb-4">
            {t.welcomeDesc(28, stats.aiAnalyses)}
          </p>
          <div className="flex gap-3">
            <Link
              to="/matters"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t.newCase}
            </Link>
            <Link
              to="/knowledge"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
            >
              <Upload className="w-4 h-4" />
              {t.uploadKnowledge}
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<FolderPlus className="w-5 h-5 text-blue-600" />}
            value={stats.activeCases}
            label={t.activeCases}
            iconBg="bg-blue-50"
            to="/matters"
          />
          <StatCard
            icon={<BookOpen className="w-5 h-5 text-purple-600" />}
            value={stats.kbCategories}
            label={t.kbCategories}
            iconBg="bg-purple-50"
            to="/knowledge"
          />
          <StatCard
            icon={<FileText className="w-5 h-5 text-green-600" />}
            value={stats.docDrafts}
            label={t.docDrafts}
            iconBg="bg-green-50"
            to="/templates"
          />
          <StatCard
            icon={<Sparkles className="w-5 h-5 text-amber-600" />}
            value={stats.aiAnalyses}
            label={t.aiAnalyses}
            iconBg="bg-amber-50"
            to="/knowledge"
          />
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Recent Cases (2/3 width) */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-brand-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-brand-800">
                {t.recentCases}
              </h3>
              <Link
                to="/matters"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                {t.viewAll}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-brand-100">
              {matters.length === 0 ? (
                <div className="py-8 text-center text-brand-400 text-sm">
                  {lang === 'zh' ? '暂无案件数据' : 'No cases yet'}
                </div>
              ) : (
                matters.slice(0, 4).map((m) => (
                  <Link key={m.matter_id} to={`/matters/${m.matter_id}`} className="block">
                    <MatterRow matter={m} lang={lang} t={t} />
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Right column: Quick Actions + KB Categories */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-brand-200 p-5">
              <h3 className="text-base font-semibold text-brand-800 mb-4">
                {t.quickActions}
              </h3>
              <div className="space-y-3">
                <QuickAction
                  icon={<Plus className="w-5 h-5" />}
                  iconBg="bg-blue-100 text-blue-600"
                  title={t.createCase}
                  desc={t.createCaseDesc}
                  to="/matters"
                />
                <QuickAction
                  icon={<Upload className="w-5 h-5" />}
                  iconBg="bg-green-100 text-green-600"
                  title={t.uploadFile}
                  desc={t.uploadFileDesc}
                  to="/knowledge"
                />
                <QuickAction
                  icon={<Search className="w-5 h-5" />}
                  iconBg="bg-navy-100 text-navy-700"
                  title={t.queryLegal}
                  desc={t.queryLegalDesc}
                  to="/legal-base"
                />
              </div>
            </div>

            {/* KB Categories */}
            <div className="bg-white rounded-xl border border-brand-200 p-5">
              <h3 className="text-base font-semibold text-brand-800 mb-4">
                {t.kbTitle}
              </h3>
              <div className="space-y-2.5">
                {kbCategories.slice(0, 5).map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/knowledge?cat=${cat.key}`}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-brand-50 transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <BookOpen className="w-4 h-4 text-brand-500" />
                      <span className="text-sm font-medium text-brand-700">
                        {lang === 'zh' ? cat.name_zh : cat.name_en}
                      </span>
                    </div>
                    <span className="text-xs text-brand-400">
                      {cat.file_count} {t.files}
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to="/knowledge"
                className="w-full mt-3 py-2 border border-dashed border-brand-300 rounded-lg text-sm text-brand-500 hover:text-brand-700 hover:border-brand-400 transition-colors flex items-center justify-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                {t.addCategory}
              </Link>
            </div>
          </div>
        </div>

        {/* Usage Tip */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-blue-800">{t.tip}</span>
            <p className="text-sm text-blue-700 mt-0.5 leading-relaxed">
              {t.tipText}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({
  icon,
  value,
  label,
  iconBg,
  to,
}: {
  icon: React.ReactNode
  value: number
  label: string
  iconBg: string
  to: string
}) {
  return (
    <Link to={to} className="bg-white rounded-xl border border-brand-200 p-4 flex items-start justify-between hover:border-brand-300 hover:shadow-sm transition-all cursor-pointer">
      <div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${iconBg}`}>
          {icon}
        </div>
        <div className="text-2xl font-bold text-brand-800">{value}</div>
        <div className="text-xs text-brand-400 mt-0.5">{label}</div>
      </div>
    </Link>
  )
}

function MatterRow({
  matter,
  lang,
  t,
}: {
  matter: Matter
  lang: string
  t: (typeof LABELS)['zh']
}) {
  const statusLabel =
    matter.status === 'active' ? t.statusActive
    : matter.status === 'planning' ? t.statusPlanning
    : t.statusClosed
  const statusColor =
    matter.status === 'active'
      ? 'bg-blue-100 text-blue-700'
      : matter.status === 'planning'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-green-100 text-green-700'

  return (
    <div className="flex items-center gap-4 py-3.5">
      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
        <FolderPlus className="w-4 h-4 text-brand-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-brand-800 truncate">
            {matter.title}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}
          >
            {statusLabel}
          </span>
        </div>
        <p className="text-xs text-brand-400 truncate mt-0.5">
          {matter.summary ?? (lang === 'zh' ? '暂无摘要' : 'No summary')}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-xs font-mono text-brand-500">
          {matter.case_number}
        </div>
        <div className="text-[11px] text-brand-400">{matter.opened_at}</div>
      </div>
    </div>
  )
}

function QuickAction({
  icon,
  iconBg,
  title,
  desc,
  to,
}: {
  icon: React.ReactNode
  iconBg: string
  title: string
  desc: string
  to: string
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-50 transition-colors group"
    >
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-brand-800">{title}</div>
        <div className="text-xs text-brand-400">{desc}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-brand-300 group-hover:text-brand-500 transition-colors" />
    </Link>
  )
}
